import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Package, AlertTriangle, TrendingDown, Search, Filter, 
  Calendar, Download, Trash2, Eye, X, Plus, Minus,
  ShieldAlert, Skull, AlertCircle, Clock, ArrowLeftRight, ClipboardCheck
} from 'lucide-react';
import { toast } from '../lib/toast';

const ADJUSTMENT_TYPES = [
  { id: 'merma', name: 'Merma', icon: TrendingDown, color: 'text-orange-500', bgColor: 'bg-orange-100', description: 'Producto dañado o en mal estado' },
  { id: 'robo', name: 'Robo', icon: Skull, color: 'text-red-600', bgColor: 'bg-red-100', description: 'Producto extraviado o robado' },
  { id: 'vencido', name: 'Vencido', icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-100', description: 'Producto caducado' },
  { id: 'ajuste', name: 'Ajuste', icon: AlertCircle, color: 'text-blue-500', bgColor: 'bg-blue-100', description: 'Corrección de inventario' },
  { id: 'conteo_fisico', name: 'Conteo Físico', icon: ClipboardCheck, color: 'text-purple-500', bgColor: 'bg-purple-100', description: 'Diferencia en conteo' },
  { id: 'transferencia', name: 'Transferencia', icon: ArrowLeftRight, color: 'text-green-500', bgColor: 'bg-green-100', description: 'Movimiento entre áreas' }
];

const InventoryAdjustments = () => {
  const { api } = useAuth();
  const [adjustments, setAdjustments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    adjustment_type: '',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [adjustmentsRes, summaryRes] = await Promise.all([
        api.get('/stock-adjustments', { params: filters }),
        api.get('/stock-adjustments/summary', { params: { start_date: filters.start_date, end_date: filters.end_date } })
      ]);
      setAdjustments(adjustmentsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Error al cargar ajustes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setLoading(true);
    fetchData();
  };

  const clearFilters = () => {
    setFilters({ adjustment_type: '', search: '', start_date: '', end_date: '' });
    setLoading(true);
    setTimeout(fetchData, 100);
  };

  const getTypeInfo = (type) => ADJUSTMENT_TYPES.find(t => t.id === type) || ADJUSTMENT_TYPES[3];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
    <Sidebar />
    <main className="flex-1 md:ml-64 min-h-screen p-6">
    <div className="space-y-6" data-testid="inventory-adjustments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-accent" />
            Ajustes de Inventario
          </h1>
          <p className="text-muted-foreground">Control de mermas, robos y ajustes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-2" />
            Filtros
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {ADJUSTMENT_TYPES.map(type => {
            const data = summary.summary[type.id] || { count: 0, total_cost: 0 };
            const Icon = type.icon;
            return (
              <Card key={type.id} className={`${type.bgColor} border-none`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${type.color}`} />
                    <span className="font-medium text-sm">{type.name}</span>
                  </div>
                  <p className="text-2xl font-bold">{data.count}</p>
                  <p className={`text-sm ${type.color}`}>
                    ${data.total_cost?.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Total Loss Card */}
      {summary && summary.total_loss > 0 && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-sm text-red-600 font-medium">Pérdida Total (Merma + Robo + Vencido)</p>
                <p className="text-3xl font-bold text-red-700">${summary.total_loss?.toLocaleString()}</p>
              </div>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              {summary.total_adjustments} ajustes registrados
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Nombre del producto..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label>Tipo de Ajuste</Label>
                <select
                  value={filters.adjustment_type}
                  onChange={(e) => setFilters({...filters, adjustment_type: e.target.value})}
                  className="w-full h-10 border rounded-md px-3"
                >
                  <option value="">Todos</option>
                  {ADJUSTMENT_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Desde</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Hasta</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleFilter}>Aplicar Filtros</Button>
              <Button variant="outline" onClick={clearFilters}>Limpiar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Adjustments List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historial de Ajustes</CardTitle>
        </CardHeader>
        <CardContent>
          {adjustments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No hay ajustes registrados</p>
              <p className="text-sm">Los ajustes se registran desde Stock por Área</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-3 font-medium">Fecha</th>
                    <th className="pb-3 font-medium">Tipo</th>
                    <th className="pb-3 font-medium">Producto</th>
                    <th className="pb-3 font-medium">Cantidad</th>
                    <th className="pb-3 font-medium">Costo</th>
                    <th className="pb-3 font-medium">Motivo</th>
                    <th className="pb-3 font-medium">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map(adj => {
                    const typeInfo = getTypeInfo(adj.adjustment_type);
                    const Icon = typeInfo.icon;
                    return (
                      <tr key={adj.adjustment_id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-3 text-sm">{formatDate(adj.created_at)}</td>
                        <td className="py-3">
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${typeInfo.bgColor} ${typeInfo.color}`}>
                            <Icon className="w-3 h-3" />
                            {typeInfo.name}
                          </div>
                        </td>
                        <td className="py-3 font-medium">{adj.item_name}</td>
                        <td className="py-3">
                          <span className={adj.quantity < 0 ? 'text-red-600' : 'text-green-600'}>
                            {adj.quantity > 0 ? '+' : ''}{adj.quantity} {adj.unit}
                          </span>
                          <span className="text-xs text-muted-foreground block">
                            {adj.previous_stock} → {adj.new_stock}
                          </span>
                        </td>
                        <td className="py-3 text-red-600 font-medium">
                          ${adj.total_cost?.toLocaleString()}
                        </td>
                        <td className="py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                          {adj.reason}
                        </td>
                        <td className="py-3 text-sm text-muted-foreground">{adj.created_by}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Loss Items by Type */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['merma', 'robo', 'vencido'].map(type => {
            const typeInfo = getTypeInfo(type);
            const data = summary.summary[type];
            const Icon = typeInfo.icon;
            
            if (!data || data.items.length === 0) return null;
            
            return (
              <Card key={type}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm flex items-center gap-2 ${typeInfo.color}`}>
                    <Icon className="w-4 h-4" />
                    Top {typeInfo.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {data.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="truncate">{item.name}</span>
                        <span className="text-red-500 font-medium">${item.cost?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </main>
    </div>
  );
};

export default InventoryAdjustments;
